import { ledgerPaidAud, onFileExpiryFrom } from './cancelSplit';

/**
 * Where a deposit's money stands, as the reports read it (B10 / 14ym9bv7652,
 * 14ym9bv75nd). Read-only selectors shared by the web reports (and any server
 * view that needs them), so the held pool, the "deposit, no date" cohort and
 * money at risk all use ONE rule.
 *
 * The "on file" rule lives in `isDepositOnFile` alone. Lane A's B4 deposit
 * state machine will own the canonical on-file / 12-month rule: swap this one
 * function for it at integration and every report follows.
 */

export interface StandingDeposit {
  id: string;
  status: string;
  amountAud: number;
  amountPaid?: number;
  ledger?: ReadonlyArray<{ kind: string; amountAud: number; at: string }>;
  bookingId?: string;
  heldBookingId?: string;
  paidAt?: string;
  expiresAt: string;
}

export interface StandingBooking {
  id: string;
  status: string;
  depositId?: string;
}

/**
 * - `booked-in`: money on a live booking (held, confirmed, on the day).
 * - `on-file`: money with no booking at all: the Sweeper's "deposit, no date".
 * - `needs-decision`: money still linked only to a cancelled / no-show / moved
 *   booking: the cancel split never settled it (B4 resolves these).
 * - `not-held`: no money in, used up by a completed session, or no longer the
 *   client's (expired / refunded / forfeited / rolled over).
 */
export type DepositStanding = 'booked-in' | 'on-file' | 'needs-decision' | 'not-held';

const DEAD_BOOKING: ReadonlySet<string> = new Set(['cancelled', 'no-show', 'superseded']);

/**
 * Money a deposit is holding for the client right now, on a paid or pending
 * deposit that hasn't been used up (`redeemed`). With a ledger it is the ledger
 * net (paid + top-ups + rollovers in + reinstates − rollovers out − forfeits), so
 * a part-paid pending link counts its part and a forfeited no-show counts $0
 * even though its `amountPaid` and status are untouched. (The link sweep's
 * `depositMoneyInAud` takes the larger of net and `amountPaid` to protect a
 * link; that is the wrong rule for a liability.)
 */
export function heldMoneyAud(d: StandingDeposit): number {
  if (d.status !== 'paid' && d.status !== 'pending') return 0;
  if ((d.ledger ?? []).some((e) => e.kind === 'redeemed')) return 0;
  return Math.max(
    0,
    ledgerPaidAud({
      ...(d.ledger ? { ledger: d.ledger.map((e) => ({ ...e })) } : {}),
      ...(d.amountPaid !== undefined ? { amountPaid: d.amountPaid } : {}),
      status: d.status,
      amountAud: d.amountAud,
    }),
  );
}

/** Bookings keyed by the deposit they point at, for the reverse link. Build once per report. */
export function bookingsByDepositId<B extends StandingBooking>(bookings: Record<string, B>): Map<string, B[]> {
  const out = new Map<string, B[]>();
  for (const b of Object.values(bookings)) {
    if (!b.depositId) continue;
    const list = out.get(b.depositId);
    if (list) list.push(b);
    else out.set(b.depositId, [b]);
  }
  return out;
}

export function depositStanding(
  d: StandingDeposit,
  bookings: Record<string, StandingBooking>,
  byDeposit: Map<string, StandingBooking[]> = bookingsByDepositId(bookings),
): DepositStanding {
  if (heldMoneyAud(d) <= 0) return 'not-held';
  // The deposit's own links, and bookings pointing back at it. Settling flows
  // (keep on file after a cancel, the no-date release, reinstate) clear only the
  // deposit's side, so a dead booking still pointing back means nothing; only the
  // deposit's own link to a dead booking leaves it undecided.
  const own = [d.bookingId, d.heldBookingId].map((id) => (id ? bookings[id] : undefined)).filter(Boolean) as StandingBooking[];
  const reverse = byDeposit.get(d.id) ?? [];
  const all = [...own, ...reverse];
  // A completed session used the deposit (the pool's long-standing redemption rule).
  if (all.some((b) => b.status === 'completed')) return 'not-held';
  if (all.some((b) => !DEAD_BOOKING.has(b.status))) return 'booked-in';
  return own.length > 0 ? 'needs-decision' : 'on-file';
}

/**
 * THE "deposit on file, no date" rule (diagram 02: the abyss, valid 12 months;
 * diagram 08: Sweeper rung 2). Money in, and no booking at all. Swap for B4's
 * rule at integration.
 */
export function isDepositOnFile(
  d: StandingDeposit,
  bookings: Record<string, StandingBooking>,
  byDeposit?: Map<string, StandingBooking[]>,
): boolean {
  return depositStanding(d, bookings, byDeposit) === 'on-file';
}

/**
 * When an on-file deposit's 12-month validity runs out: 12 months from when it
 * was paid, or a later stamp (`keepOnFile` restamps the expiry when a booking is
 * cancelled). A stale link expiry never shortens it.
 */
export function onFileValidUntil(d: Pick<StandingDeposit, 'expiresAt' | 'paidAt' | 'ledger'>): string {
  const firstIn = d.paidAt ?? d.ledger?.find((e) => e.kind === 'paid' || e.kind === 'topup')?.at;
  const fromPaid = firstIn ? Date.parse(onFileExpiryFrom(firstIn)) : 0;
  return new Date(Math.max(Date.parse(d.expiresAt), fromPaid)).toISOString();
}
